import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { endpoints } from "@/api/endpoints";
import type { ApiError } from "@/api/types";
import { useAuthStore } from "@/stores/authStore";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * bizpandit-api issues the JWT access/refresh tokens as httpOnly, secure,
 * sameSite cookies only — it never returns them in a JSON response body
 * (see bizpandit-api/README.md "Security notes"). So there is nothing for
 * this client to store or attach as an `Authorization` header; the browser
 * sends the session cookie automatically as long as `withCredentials` is
 * set. `useAuthStore` used to hold a bearer token pulled out of the login
 * response — that token was never real (see auth.service.ts's fixed
 * `login()`), so this was silently non-functional against the real
 * backend. Fixed here rather than worked around, since every hook built on
 * top of this client depends on requests actually being authenticated.
 */
export const apiClient = axios.create({
  baseURL,
  timeout: 15_000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let refreshQueue: Array<(ok: boolean) => void> = [];

function resolveQueue(ok: boolean) {
  refreshQueue.forEach((cb) => cb(ok));
  refreshQueue = [];
}

/**
 * Endpoints where a 401 means "this specific credential/action was
 * rejected" (wrong password, unverified OTP, etc.) — NOT "your session
 * token expired." The auto-refresh-and-retry below must never fire for
 * these, or a plain "wrong password" on login gets silently swapped out
 * for a confusing "Missing refresh token" error: the interceptor used to
 * treat every 401 (except /auth/refresh itself) as an expired-session
 * case, so a failed login attempt triggered a refresh call, which itself
 * 401'd (no session cookie exists yet — the user was never logged in),
 * and that secondary error overwrote the real "invalid credentials"
 * message the user actually needed to see. Bug found 2026-07-16.
 */
const AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH: string[] = [
  endpoints.auth.refresh,
  endpoints.auth.login,
  endpoints.auth.register,
  endpoints.auth.sendOtp,
  endpoints.auth.verifyOtp,
  endpoints.register,
];

/**
 * Normalizes errors into ApiError and transparently retries the original
 * request once after a successful cookie-based token refresh on a 401.
 * `POST /auth/refresh` rotates the refresh cookie and sets a new access
 * cookie via `Set-Cookie` — there's no token in the response body to read
 * or forward, the retried request just picks up the new cookie
 * automatically.
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; errors?: { code?: string; details?: unknown } }>) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status ?? 0;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH.includes(originalRequest.url ?? "")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((ok) => (ok ? resolve(apiClient(originalRequest)) : reject(error)));
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await apiClient.post(endpoints.auth.refresh);
        resolveQueue(true);
        return apiClient(originalRequest);
      } catch (refreshError) {
        resolveQueue(false);
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const apiError: ApiError = {
      status,
      message: error.response?.data?.message ?? error.message ?? "Unexpected error",
      code: error.response?.data?.errors?.code,
      fieldErrors: undefined,
    };

    return Promise.reject(apiError);
  },
);
